import { Box, Container, Title, SimpleGrid, List, Divider, Group } from '@mantine/core'
import {
  IoShieldCheckmarkOutline,
  IoConstructOutline,
  IoChatbubblesOutline,
  IoFlash
} from 'react-icons/io5'
import { AppText, AppStack, AppCard, AmmoScoreBadge, SiteLegalFooter, GunSlopWordmark } from '../../app/common/ui'
import bulletPointIconUrl from '../../app/icon-9mm-bulletpoint/icon-styled.svg?url'

const CROSSHAIR = {
  backgroundImage:
    'repeating-linear-gradient(0deg, rgba(232,93,4,0.04) 0px, transparent 1px, transparent 20px), repeating-linear-gradient(90deg, rgba(232,93,4,0.04) 0px, transparent 1px, transparent 20px)'
}

const PRINCIPLES: Array<{ title: string; body: string; icon: React.ReactNode }> = [
  {
    title: 'Community-verified information',
    body: 'Every firearm in the Registry, every part in the Parts Bin, and every build shared on GunSlop comes from the community. Users contribute, verify, and improve the data. The AmmoScore system ensures that the most reliable contributors earn standing on the platform.',
    icon: <IoShieldCheckmarkOutline size={26} color="var(--gs-accent)" />
  },
  {
    title: 'Built for real use',
    body: 'We exist to solve problems that gun owners actually face. Finding accurate specifications on a specific firearm. Understanding part compatibility. Seeing how others have configured similar builds. These are the things GunSlop makes simple.',
    icon: <IoConstructOutline size={26} color="var(--gs-accent)" />
  },
  {
    title: 'All firearm discussions welcome',
    body: "GunSlop is a space for firearm-related conversation. Whether you're discussing technical specifications, design choices, parts selection, regulation, or anything else related to firearms — that discussion belongs here. Stay on topic. That's the only rule.",
    icon: <IoChatbubblesOutline size={26} color="var(--gs-accent)" />
  }
]

const PROVIDES = [
  'A searchable Registry of firearms with community-verified data',
  'A Parts Bin documenting compatibility and real-world information',
  'Build documentation from owners showing complete configurations',
  'A reputation system that surfaces reliable voices',
  'Tools to share your gear safely, including serial number detection'
]

const TIERS: Array<{
  name: string
  sampleScore: number
  range: string
  color: string
  bg: string
  border: string
  unlocks: string
}> = [
  {
    name: 'Newcomer',
    sampleScore: 5,
    range: '0 – 9',
    color: '#8B949E',
    bg: 'rgba(139,148,158,0.10)',
    border: 'rgba(139,148,158,0.25)',
    unlocks: 'Browse the Registry and read all platform content'
  },
  {
    name: 'Enthusiast',
    sampleScore: 25,
    range: '10 – 49',
    color: '#58A6FF',
    bg: 'rgba(88,166,255,0.10)',
    border: 'rgba(88,166,255,0.25)',
    unlocks: 'Create posts, reviews, tips, and discussion threads'
  },
  {
    name: 'Contributor',
    sampleScore: 75,
    range: '50 – 99',
    color: '#3FB950',
    bg: 'rgba(63,185,80,0.10)',
    border: 'rgba(63,185,80,0.25)',
    unlocks: 'Edit tags on firearm Registry models'
  },
  {
    name: 'Expert',
    sampleScore: 150,
    range: '100 – 199',
    color: '#E85D04',
    bg: 'rgba(232,93,4,0.12)',
    border: 'rgba(232,93,4,0.35)',
    unlocks: 'Submit registry model edits for admin review'
  },
  {
    name: 'Curator',
    sampleScore: 350,
    range: '200 – 499',
    color: '#C9A84C',
    bg: 'rgba(201,168,76,0.12)',
    border: 'rgba(201,168,76,0.35)',
    unlocks: 'Trusted curator standing — contributions carry elevated weight'
  },
  {
    name: 'Armorer',
    sampleScore: 750,
    range: '500+',
    color: '#C9A84C',
    bg: 'linear-gradient(135deg, rgba(232,93,4,0.18), rgba(201,168,76,0.18))',
    border: 'rgba(201,168,76,0.5)',
    unlocks: 'Top-tier standing. The most trusted voice on the platform'
  }
]

const EARN_EVENTS = [
  { label: 'Part submission approved', points: '+10' },
  { label: 'Reloader recipe approved', points: '+10' },
  { label: 'Registry model approved', points: '+8' },
  { label: 'Cartridge spec approved', points: '+5' },
  { label: 'Ammo product approved', points: '+5' },
  { label: 'Post upvoted by the community', points: '+4' },
  { label: 'Part or model upvoted', points: '+3' },
  { label: 'Recipe validation submitted', points: '+3' },
  { label: 'Build or comment upvoted', points: '+2' }
]

const LOSE_EVENTS = [
  { label: 'Content removed by moderation', points: '−20' },
  { label: 'Content flagged with a warning', points: '−10' },
  { label: 'Part submission rejected', points: '−5' },
  { label: 'Reloader recipe rejected', points: '−5' },
  { label: 'Registry model rejected', points: '−3' },
  { label: 'Post downvoted', points: '−2' },
  { label: 'Cartridge spec or ammo product rejected', points: '−2' },
  { label: 'Part, model, build, or comment downvoted', points: '−1' }
]

export default function AboutUsPage () {
  return (
    <Box sx={{ marginTop: -8 }}>

      {/* ── Hero ── */}
      <Box
        sx={{
          width: '100%',
          ...CROSSHAIR,
          padding: '48px 20px 56px',
          borderBottom: '1px solid var(--gs-border)'
        }}
        style={{ background: 'var(--gs-bg)' }}
      >
        <Container size="sm">
          <AppStack spacing="lg" align="center">
            <Group spacing={6} sx={{ alignItems: 'baseline' }}>
              <AppText
                sx={{
                  fontFamily: 'Oswald, sans-serif',
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: 3,
                  textTransform: 'uppercase',
                  color: 'var(--gs-muted)'
                }}
              >
                About
              </AppText>
              <GunSlopWordmark
                size="xs"
                inline
                sx={{
                  fontSize: 11,
                  letterSpacing: 2,
                  color: 'var(--gs-muted)'
                }}
              />
            </Group>
            <Title
              order={1}
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(28px, 4.5vw, 40px)',
                color: 'var(--gs-text)',
                textAlign: 'center',
                lineHeight: 1.15
              }}
            >
              A community platform for
              <br />
              <AppText component="span" sx={{ color: 'var(--gs-accent)' }}>
                firearm lovers
              </AppText>
            </Title>
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 16,
                color: 'var(--gs-muted)',
                textAlign: 'center',
                maxWidth: 520,
                lineHeight: 1.7
              }}
            >
              GunSlop is a community platform built for firearm enthusiasts. We provide tools to document, verify,
              and share knowledge about firearms, components, and builds. Our goal is to make that information
              accessible and reliable.
            </AppText>
          </AppStack>
        </Container>
      </Box>

      {/* ── Core principles ── */}
      <Box sx={{ padding: '48px 20px 56px' }}>
        <Container size="lg">
          <SimpleGrid cols={3} spacing="lg" breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
            {PRINCIPLES.map((p) => (
              <AppCard
                key={p.title}
                sx={{
                  padding: '22px 20px',
                  background: 'var(--gs-surface)',
                  transition: 'border-color 150ms ease',
                  '&:hover': { borderColor: 'var(--gs-border-bright)' }
                }}
              >
                <Box sx={{ marginBottom: 14 }}>{p.icon}</Box>
                <AppText
                  sx={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 600,
                    fontSize: 15,
                    letterSpacing: 0.3,
                    color: 'var(--gs-text)',
                    marginBottom: 12,
                    lineHeight: 1.25
                  }}
                >
                  {p.title}
                </AppText>
                <AppText
                  sx={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 14,
                    color: 'var(--gs-muted)',
                    lineHeight: 1.65
                  }}
                >
                  {p.body}
                </AppText>
              </AppCard>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── What we provide ── */}
      <Box
        sx={{
          padding: '48px 20px 56px',
          background: 'var(--gs-surface)',
          borderTop: '1px solid var(--gs-border)',
          borderBottom: '1px solid var(--gs-border)'
        }}
      >
        <Container size="md">
          <Title
            order={2}
            sx={{
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 2.5,
              textTransform: 'uppercase',
              color: 'var(--gs-muted)',
              marginBottom: 20
            }}
          >
            What we provide
          </Title>
          <List
            spacing="sm"
            size="sm"
            icon={
              <Box
                component="img"
                src={bulletPointIconUrl}
                alt=""
                aria-hidden
                sx={{
                  width: 22,
                  height: 22,
                  display: 'block',
                  objectFit: 'contain',
                  flexShrink: 0
                }}
              />
            }
            styles={{
              item: { alignItems: 'flex-start' },
              itemWrapper: { width: '100%' }
            }}
          >
            {PROVIDES.map((line) => (
              <List.Item key={line}>
                <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 15, color: 'var(--gs-text)', lineHeight: 1.55 }}>
                  {line}
                </AppText>
              </List.Item>
            ))}
          </List>
        </Container>
      </Box>

      {/* ── AmmoScore System ── */}
      <Box
        sx={{
          padding: '56px 20px 64px',
          ...CROSSHAIR,
          borderTop: '1px solid var(--gs-border)'
        }}
        style={{ background: 'var(--gs-bg)' }}
      >
        <Container size="lg">

          {/* Section header */}
          <AppStack spacing={0} align="center" sx={{ marginBottom: 48 }}>
            <AppText
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: 3,
                textTransform: 'uppercase',
                color: 'var(--gs-accent)',
                marginBottom: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <IoFlash size={11} />
              The AmmoScore System
            </AppText>
            <Title
              order={2}
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(22px, 3.5vw, 32px)',
                color: 'var(--gs-text)',
                textAlign: 'center',
                lineHeight: 1.2,
                marginBottom: 16
              }}
            >
              Reputation that reflects real contribution
            </Title>
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 15,
                color: 'var(--gs-muted)',
                textAlign: 'center',
                maxWidth: 580,
                lineHeight: 1.7
              }}
            >
              AmmoScore is GunSlop's proprietary reputation system. Every vote, submission, and moderation
              outcome on the platform contributes to a member's score. Score determines tier, and tier
              determines what a member can do — a trust structure built entirely from demonstrated
              participation, not seniority.
            </AppText>
          </AppStack>

          {/* Tier cards */}
          <SimpleGrid
            cols={3}
            spacing="md"
            breakpoints={[
              { maxWidth: 'md', cols: 2 },
              { maxWidth: 'sm', cols: 1 }
            ]}
            sx={{ marginBottom: 40 }}
          >
            {TIERS.map((tier) => (
              <Box
                key={tier.name}
                sx={{
                  padding: '18px 16px 20px',
                  background: tier.bg,
                  border: `1px solid ${tier.border}`,
                  borderRadius: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <AmmoScoreBadge tier={tier.name} score={tier.sampleScore} size="sm" />
                  <AppText
                    sx={{
                      fontFamily: 'Oswald, sans-serif',
                      fontWeight: 600,
                      fontSize: 11,
                      color: tier.color,
                      letterSpacing: 0.5,
                      opacity: 0.9,
                      background: tier.bg,
                      border: `1px solid ${tier.border}`,
                      padding: '2px 7px',
                      borderRadius: 4,
                      flexShrink: 0
                    }}
                  >
                    {tier.range}
                  </AppText>
                </Box>
                <Divider sx={{ borderColor: tier.border }} />
                <AppText
                  sx={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 13,
                    color: 'var(--gs-muted)',
                    lineHeight: 1.6
                  }}
                >
                  {tier.unlocks}
                </AppText>
              </Box>
            ))}
          </SimpleGrid>

          {/* Points breakdown */}
          <AppCard sx={{ padding: '28px 28px 24px' }}>
            <AppText
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: 2.5,
                textTransform: 'uppercase',
                color: 'var(--gs-muted)',
                marginBottom: 22
              }}
            >
              How scores are calculated
            </AppText>

            <SimpleGrid
              cols={2}
              spacing={40}
              breakpoints={[{ maxWidth: 'sm', cols: 1 }]}
            >
              {/* Earn column */}
              <Box>
                <AppText
                  sx={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 600,
                    fontSize: 11,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    color: '#3FB950',
                    marginBottom: 12
                  }}
                >
                  Earn points
                </AppText>
                <AppStack spacing={0}>
                  {EARN_EVENTS.map((e) => (
                    <Box
                      key={e.label}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 12,
                        padding: '7px 0',
                        borderBottom: '1px solid var(--gs-border)',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <AppText
                        sx={{
                          fontFamily: 'DM Sans, sans-serif',
                          fontSize: 13,
                          color: 'var(--gs-muted)',
                          lineHeight: 1.4
                        }}
                      >
                        {e.label}
                      </AppText>
                      <AppText
                        sx={{
                          fontFamily: 'Oswald, sans-serif',
                          fontWeight: 700,
                          fontSize: 13,
                          color: '#3FB950',
                          flexShrink: 0
                        }}
                      >
                        {e.points}
                      </AppText>
                    </Box>
                  ))}
                </AppStack>
              </Box>

              {/* Lose column */}
              <Box>
                <AppText
                  sx={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 600,
                    fontSize: 11,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    color: '#F85149',
                    marginBottom: 12
                  }}
                >
                  Lose points
                </AppText>
                <AppStack spacing={0}>
                  {LOSE_EVENTS.map((e) => (
                    <Box
                      key={e.label}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 12,
                        padding: '7px 0',
                        borderBottom: '1px solid var(--gs-border)',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <AppText
                        sx={{
                          fontFamily: 'DM Sans, sans-serif',
                          fontSize: 13,
                          color: 'var(--gs-muted)',
                          lineHeight: 1.4
                        }}
                      >
                        {e.label}
                      </AppText>
                      <AppText
                        sx={{
                          fontFamily: 'Oswald, sans-serif',
                          fontWeight: 700,
                          fontSize: 13,
                          color: '#F85149',
                          flexShrink: 0
                        }}
                      >
                        {e.points}
                      </AppText>
                    </Box>
                  ))}
                </AppStack>
              </Box>
            </SimpleGrid>

            <Box sx={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid var(--gs-border)' }}>
              <AppText
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 12,
                  color: 'var(--gs-muted)',
                  lineHeight: 1.6,
                  opacity: 0.7
                }}
              >
                Scores never go below zero. Photo votes are aesthetic signals only and do not affect AmmoScore.
                Admins may apply manual adjustments for conduct not captured by automated events.
              </AppText>
            </Box>
          </AppCard>

        </Container>
      </Box>

      <SiteLegalFooter />
    </Box>
  )
}
